import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, Minus, Plus, ShieldAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import apiClient from '@/api/client'
import { mergePricingSettings } from '@/lib/counterPricing'
import { useFiscalStatus } from '@/hooks/useFiscalStatus'
import type { OrdersBrowserRow, FiscalNote } from '@/types'

interface Props {
  order: OrdersBrowserRow
  /** Retained for callsite parity with the pre-refactor API; only `'credit'` is honored. */
  noteType: 'credit' | 'debit'
  onClose: () => void
  onSuccess: (note: FiscalNote) => void
}

interface CreditLine {
  product_name: string
  /** Sent with the refund so the backend can tell a refunded coffee from a
   *  refunded dessert when it re-grades loyalty stamps. Name matching is the
   *  server-side fallback and is ambiguous when two products share a name. */
  product_id?: string
  /** Quantity originally on the invoice (max we can refund). */
  max_quantity: number
  /** Editable: how many of this line we're refunding now. */
  refund_quantity: number
  unit_price: number
  /** Optional PCT (HS) code from the product master — needed when we
   *  fall back to the per-line postFiscalNote endpoint for partial
   *  refunds (the PIN-gated full-credit endpoint reuses the order's
   *  own line PCTs server-side, so we only need this for partials). */
  pct_code?: string
}

/**
 * CreditNoteDialog (file kept under the original name for import-stability)
 * is the PIN-gated credit-note replacement for the destructive
 * "cancel a synced invoice" path.
 *
 * Two modes:
 *
 *   • Full credit  — every line is fully refunded (the default when the
 *     dialog opens). Uses the PIN-gated /admin/orders/:id/credit-note
 *     endpoint that mirrors the order's items server-side.
 *
 *   • Partial credit — operator dials in per-line refund quantities; we
 *     POST /admin/fiscal/notes with the selected lines so FBR sees a
 *     credit note that exactly matches the items being returned. The
 *     original order stays in `completed` status (audit-first); the
 *     reports surface the offsetting credit total like a full credit.
 */
export function FiscalNoteDialog({ order, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState('')
  const [lines, setLines] = useState<CreditLine[]>([])
  const { enabled: fiscalEnabled, authority: fiscalAuthority } = useFiscalStatus()
  const noun = fiscalEnabled ? 'Credit Note' : 'Refund'
  const verb = fiscalEnabled ? 'credit' : 'refund'

  const orderQuery = useQuery({
    queryKey: ['order-for-credit-note', order.id],
    queryFn: () => apiClient.getOrder(order.id),
  })

  // Initialise lines when the order data lands. Default each line's
  // refund_quantity to its max so a 1-click "Issue full credit" still
  // works without any extra UI interaction.
  useEffect(() => {
    const items = orderQuery.data?.data?.items ?? []
    const next = items
      .filter((it) => it.status !== 'voided')
      .map<CreditLine>((it) => ({
        product_name: it.product?.name ?? `Item ${it.product_id}`,
        product_id: it.product_id,
        max_quantity: it.quantity,
        refund_quantity: it.quantity,
        unit_price: it.unit_price,
        pct_code: it.product?.pct_code,
      }))
    setLines(next)
  }, [orderQuery.data])

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + l.refund_quantity * l.unit_price, 0),
    [lines],
  )

  // Name the authority the note is actually going to. This dialog said "FBR"
  // unconditionally, so a PRA tenant was told its refund goes to the wrong
  // regulator on the screen where a manager approves it.
  const authorityLabel = fiscalAuthority === 'PRA_EIMS' ? 'PRA' : 'FBR'

  // The flag decides whether delivery sits inside the tax base, so the
  // effective rate cannot be derived without it.
  const pricingQuery = useQuery({
    queryKey: ['counter', 'pricing'],
    queryFn: () => apiClient.getCounterPricing(),
    staleTime: 60_000,
  })

  /**
   * Effective rate of the ORIGINAL invoice, mirroring the server's
   * inferOrderTaxRate (handlers/fiscal_notes.go).
   *
   * This previously divided tax by `subtotal` alone. On any discounted order
   * that denominator is too large, so the rate came out low and the dialog
   * UNDERSTATED the credit — a manager approved one number and a different one
   * was filed. That is the same F-MM-06 defect the server fixed; the comment
   * here claimed to match the server while not doing so.
   *
   * The base must be what pricing.ComputeTotalsEx actually taxed:
   *   (subtotal - order discount - per-line discounts) + delivery
   * with delivery dropped when the tenant flag excludes it from the base
   * (F-MM-N1).
   */
  const taxRate = useMemo(() => {
    const o = orderQuery.data?.data
    if (!o) return 0
    const tax = o.tax_amount ?? 0
    if (tax <= 0) return 0

    const ps = mergePricingSettings(pricingQuery.data?.data)
    const taxable = Math.max(
      0,
      (o.subtotal ?? 0) - (o.discount_amount ?? 0) - (o.item_discounts_total ?? 0),
    )
    let base = taxable
    if (!ps.delivery_excluded_from_tax_base) base += o.delivery_fee_amount ?? 0

    if (base <= 0.001) return 0
    return tax / base
  }, [orderQuery.data, pricingQuery.data])

  const taxAmount = subtotal * taxRate
  const total = subtotal + taxAmount

  // Detect partial vs full so we can pick the right endpoint and display
  // the correct mode label. A "full credit" is every line refunded at
  // its original quantity AND at least one line being refunded.
  const totalRefundQty = lines.reduce((sum, l) => sum + l.refund_quantity, 0)
  const totalMaxQty = lines.reduce((sum, l) => sum + l.max_quantity, 0)
  const isPartial = totalRefundQty < totalMaxQty
  const isEmpty = totalRefundQty === 0

  const mutation = useMutation({
    mutationFn: () => {
      const trimmedPin = pin.trim()
      const trimmedReason = reason.trim()
      if (isPartial) {
        // Per-line credit note via the generic fiscal-notes endpoint.
        // We exclude any line with refund_quantity = 0 so FBR receives
        // exactly the items being returned.
        return apiClient.postFiscalNote({
          order_id: order.id,
          note_type: 'credit',
          reason: trimmedReason,
          pin: trimmedPin,
          items: lines
            .filter((l) => l.refund_quantity > 0)
            .map((l) => ({
              product_name: l.product_name,
              product_id: l.product_id,
              quantity: l.refund_quantity,
              unit_price: l.unit_price,
              pct_code: l.pct_code,
            })),
        })
      }
      return apiClient.issueOrderCreditNote(order.id, {
        pin: trimmedPin,
        reason: trimmedReason,
      })
    },
    onSuccess: (res) => {
      if (res.success && res.data) {
        onSuccess(res.data)
      }
    },
  })

  useEffect(() => {
    setPin('')
  }, [order.id])

  const pinIsValid = /^\d{4}$/.test(pin.trim())
  const isValid =
    reason.trim().length > 0 && pinIsValid && lines.length > 0 && !isEmpty && !mutation.isPending

  const handleSubmit = () => {
    if (!isValid) return
    mutation.mutate()
  }

  const setRefundQty = (idx: number, value: number) => {
    setLines((prev) =>
      prev.map((l, i) =>
        i === idx
          ? {
              ...l,
              refund_quantity: Math.max(0, Math.min(l.max_quantity, Math.floor(value))),
            }
          : l,
      ),
    )
  }

  const refundAll = () =>
    setLines((prev) => prev.map((l) => ({ ...l, refund_quantity: l.max_quantity })))
  const refundNone = () =>
    setLines((prev) => prev.map((l) => ({ ...l, refund_quantity: 0 })))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-destructive" />
            Issue {noun}
            {isPartial && !isEmpty && (
              <Badge variant="outline" className="ml-1 border-amber-300 text-amber-700">
                Partial
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Order <span className="font-semibold">{order.order_number}</span>
            {' — '}
            {fiscalEnabled
              ? `A credit note will be posted to ${authorityLabel} (InvoiceType 3) to reverse the selected items on this synced invoice. The original record stays in your audit log; the credit note appears alongside it as the offsetting document.`
              : 'This refund will reverse the selected items and offset the original payments. The original order stays in the audit log; the refund appears alongside it as the offsetting document.'}
          </DialogDescription>
        </DialogHeader>

        {orderQuery.isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="ml-2 text-sm text-muted-foreground">Loading invoice items…</span>
          </div>
        )}

        {!orderQuery.isLoading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Items being credited (set quantity to 0 to skip)
              </Label>
              <div className="flex gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={refundAll}
                  disabled={lines.length === 0}
                >
                  Refund all
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px]"
                  onClick={refundNone}
                  disabled={lines.length === 0}
                >
                  Clear
                </Button>
              </div>
            </div>
            <div className="border rounded-md divide-y">
              {lines.length === 0 && (
                <p className="text-sm text-muted-foreground p-3">
                  No chargeable items found on this invoice.
                </p>
              )}
              {lines.map((line, idx) => {
                const refundLineTotal = line.refund_quantity * line.unit_price
                return (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{line.product_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {line.unit_price.toFixed(2)} × {line.max_quantity} ordered
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setRefundQty(idx, line.refund_quantity - 1)}
                        disabled={line.refund_quantity <= 0}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <Input
                        type="number"
                        min={0}
                        max={line.max_quantity}
                        value={line.refund_quantity}
                        onChange={(e) =>
                          setRefundQty(idx, Number.parseInt(e.target.value || '0', 10))
                        }
                        className="h-7 w-14 text-center tabular-nums"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setRefundQty(idx, line.refund_quantity + 1)}
                        disabled={line.refund_quantity >= line.max_quantity}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="text-sm tabular-nums font-medium text-destructive w-24 text-right">
                      {refundLineTotal > 0 ? `−${refundLineTotal.toFixed(2)}` : '—'}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label
              htmlFor="cn-pin"
              className="text-xs uppercase tracking-wide text-muted-foreground"
            >
              Admin / Manager PIN (4 digits)
            </Label>
            <Input
              id="cn-pin"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              className="font-mono text-center tracking-[0.5em]"
            />
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="cn-reason"
              className="text-xs uppercase tracking-wide text-muted-foreground"
            >
              Reason (required)
            </Label>
            <Input
              id="cn-reason"
              placeholder="e.g. Customer cancelled order"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="text-sm">
            <div className="text-muted-foreground">
              {fiscalEnabled ? 'Credit total' : 'Refund total'}
              {isPartial && !isEmpty ? ' (partial)' : ''}
            </div>
            <div className="text-xl font-bold tabular-nums text-destructive">
              −{total.toFixed(2)}
            </div>
            {taxAmount > 0 && (
              <div className="text-[10px] text-muted-foreground">
                incl. tax {taxAmount.toFixed(2)}
              </div>
            )}
          </div>
          {mutation.isSuccess && mutation.data?.data && (() => {
            const note = mutation.data.data
            const hasIRN = !!note.fiscal_details?.irn
            return (
              <Badge
                variant="outline"
                className={
                  note.status === 'synced'
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                    : 'bg-amber-50 text-amber-800 border-amber-200'
                }
              >
                {note.status === 'synced'
                  ? hasIRN
                    ? `Synced — IRN: ${note.fiscal_details?.irn}`
                    : 'Refund recorded'
                  : `Status: ${note.status}`}
              </Badge>
            )
          })()}
        </div>

        {mutation.isError && (
          <p className="text-sm text-destructive">
            {(mutation.error as Error)?.message ?? `Failed to issue ${verb}`}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {mutation.isSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!mutation.isSuccess && (
            <Button
              type="button"
              variant="destructive"
              disabled={!isValid}
              onClick={handleSubmit}
            >
              {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {fiscalEnabled
                ? isPartial && !isEmpty
                  ? `Post Partial Credit Note to ${authorityLabel}`
                  : `Post Credit Note to ${authorityLabel}`
                : isPartial && !isEmpty
                  ? 'Issue Partial Refund'
                  : 'Issue Refund'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
